<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { DropdownMenuContentProps } from 'reka-ui'
import { useFilter } from 'reka-ui'
import { Search } from '@lucide/vue'

import type { DropdownSearchListData } from './types'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@ds/primitives/dropdown-menu'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@ds/primitives/input-group'

type Props = {
  data: DropdownSearchListData[]
  label?: string
  /** Highlighted in the list, e.g. the option the trigger currently reads. */
  currentValue?: string
  placeholder?: string
  /** Shown when the search matches nothing. */
  emptyLabel?: string
  side?: DropdownMenuContentProps['side']
  align?: DropdownMenuContentProps['align']
}

const {
  placeholder = 'Find an option...',
  emptyLabel = 'Nothing matches that.',
  side = 'bottom',
  align = 'start',
  ...props
} = defineProps<Props>()

type Emits = {
  'click:item': [value: string]
}

const emit = defineEmits<Emits>()

const isOpen = defineModel<boolean>('open', { default: false })

const query = ref('')

/** Reka's own matcher — accent-insensitive, so "Reserve" finds "Réserve". */
const { contains } = useFilter({ sensitivity: 'base' })

const filteredData = computed(() =>
  props.data.filter(item => contains(item.label, query.value)),
)

const handleSelect = (value: string) => {
  emit('click:item', value)
}

// The search is ours, not Reka's (a plain input, not a Combobox) — drop it
// with the menu rather than leaving it filtering a panel nobody is looking at.
watch(isOpen, open => {
  if (!open) query.value = ''
})
</script>

<template>
  <DropdownMenu v-model:open="isOpen">
    <DropdownMenuTrigger
      as-child
    >
      <slot />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      :side
      :align
      class="w-64 p-0"
    >
      <DropdownMenuLabel v-if="label">
        {{ label }}
      </DropdownMenuLabel>
      <div
        class="p-1.5 border-b border-border"
        :class="label ? 'pt-0' : ''"
      >
        <InputGroup>
          <InputGroupAddon>
            <Search class="size-4" />
          </InputGroupAddon>
          <InputGroupInput
            v-model="query"
            :placeholder
          />
        </InputGroup>
      </div>
      <div class="max-h-80 overflow-y-auto p-1">
        <DropdownMenuItem
          v-for="item in filteredData"
          :key="item.value"
          :disabled="item.disabled"
          :class="item.value === currentValue ? 'bg-accent text-accent-foreground' : ''"
          @select="handleSelect(item.value)"
        >
          {{ item.label }}
        </DropdownMenuItem>
        <p
          v-if="!filteredData.length"
          class="px-2.5 py-4 text-center text-sm font-medium text-muted-foreground"
        >
          {{ emptyLabel }}
        </p>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
